// On-demand Twilio Device registration (temporary mitigation for the shared Twilio identity).
//
// When the backend reports on_demand_registration, the Device is created at page load but is NOT
// registered. The backend sends prepare_call to one socket of the selected agent; this module
// registers the Device and acks ready. In this mode an incoming voice notification is held until
// the matching Twilio call (same call_sid, addressed to this agent) has arrived, so the popup,
// ringer and Accept only exist when the browser can actually answer. When nothing needs the
// Device any more it is unregistered so it stops using one of the shared registration slots.

import { store } from "../redux/store";
import { checkCallSid, loggerHelper, GetWebSocketConnection } from "./sharedService";
import { DtoIncomingNotification } from "../dto/dashboard.dto";

let onDemandMode = false;
let registerInFlight: Promise<void> | null = null;
let unregisterInFlight: Promise<void> | null = null;
// call_sid -> expiry (ms) for prepares whose notification may still arrive
const pendingPrepares = new Map<string, number>();
// call_sid -> notification waiting for its Twilio call
const heldNotifications = new Map<string, { notification: DtoIncomingNotification; expiresAt: number }>();

const DEFAULT_HOLD_MS = 30000;
// Used for unregister() when no prepare has supplied the backend's registration timeout yet.
const DEFAULT_LIFECYCLE_TIMEOUT_MS = 8000;
// The Device must be idle this long before it is unregistered, so short gaps between call states
// (hold: the agent leg drops before holdCallDetails is set, transfers, etc.) never unregister it.
const IDLE_GRACE_MS = 10000;
// After this many consecutive register/unregister failures the Device is recreated (only when no
// call is on it), since a Device stuck in "registering" cannot be registered again.
const MAX_LIFECYCLE_FAILURES = 2;
const ACTIVE_CALL_STATUSES = ["pending", "ringing", "connecting", "open", "reconnecting"];

let idleSince: number | null = null;
let lifecycleTimeoutMs = DEFAULT_LIFECYCLE_TIMEOUT_MS;
let consecutiveFailures = 0;
// Set when unregister() hung: the Device may say "registered" after presence was already sent as
// offline, so its state can no longer be trusted.
let deviceSuspect = false;
// Supplied by App.tsx, which owns Device creation and its listeners.
let deviceRecreator: ((token: string) => any) | null = null;

const log = (event: string, details: any = {}) => {
    loggerHelper(`[ON_DEMAND_REG] ${event}`, { ...details, at: new Date().toISOString() });
};

const getDevice = (): any => store.getState().agentNotification.device;

const callKey = (callSid: string | undefined): string => checkCallSid(callSid) || "";

export const isOnDemandMode = (): boolean => onDemandMode;

export const setOnDemandMode = (value: boolean) => {
    onDemandMode = value === true;
};

export const setDeviceRecreator = (recreator: (token: string) => any) => {
    deviceRecreator = recreator;
};

export const getCurrentAgentId = (): string | undefined => {
    try {
        const token = localStorage.getItem("access_Token");
        if (!token) return undefined;
        const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(atob(payload))?.sub;
    } catch (e) {
        return undefined;
    }
};

const getParam = (call: any, name: string): string | undefined => {
    try {
        return call?.["customParameters"]?.get(name) || undefined;
    } catch (e) {
        return undefined;
    }
};

export const getCallSidOf = (call: any): string => callKey(getParam(call, "call_sid"));

// An on-demand leg addressed to another agent (shared identity fan-out).
export const isCallForAnotherAgent = (call: any): boolean => {
    const target = getParam(call, "target_agent");
    if (!target) return false;
    const me = getCurrentAgentId();
    return !!me && target !== me;
};

// An on-demand leg addressed to this agent.
export const isCallTargetedAtMe = (call: any): boolean => {
    const target = getParam(call, "target_agent");
    return !!target && target === getCurrentAgentId();
};

// An other-agent fan-out leg: offered to every prepared other agent, so it carries no target_agent.
export const isFanoutCall = (call: any): boolean => getParam(call, "fanout") === "1";

// This browser was prepared (registered on demand) for this call.
export const hasPendingPrepare = (callSid: string): boolean => {
    const expiresAt = pendingPrepares.get(callKey(callSid));
    return !!expiresAt && expiresAt >= Date.now();
};

// In on-demand mode every new voice popup waits for its Twilio call (acknowledgements do not).
export const isOnDemandNotification = (notification: DtoIncomingNotification): boolean =>
    onDemandMode && notification?.call_type === "voice" && !notification?.isAcknowledged;

// True when this browser holds the Twilio call for this notification's call_sid.
export const hasMatchingCallInstance = (notification: DtoIncomingNotification): boolean => {
    const device = getDevice();
    if (!device || device.state !== "registered") return false;
    const instances: any[] = store.getState().agentNotification.voiceCallInstance || [];
    return instances.some((call: any) =>
        getCallSidOf(call) === callKey(notification.call_sid) && !isCallForAnotherAgent(call)
    );
};

// Marks a popup shown through the gate, so Accept can explain a missing call accurately.
export const markOnDemand = (notification: DtoIncomingNotification): DtoIncomingNotification =>
    ({ ...notification, on_demand: true });

// The targeted (or fan-out) Twilio call reached this browser: the backend starts the answer window on this.
export const reportIncomingReceived = (call: any) => {
    const callSid = getCallSidOf(call);
    log("incoming_received", { call_sid: callSid });
    try {
        GetWebSocketConnection()?.emit("incoming_received", { call_sid: callSid });
    } catch (e) {
        log("incoming_received_emit_failed", { call_sid: callSid, message: (e as any)?.message });
    }
};

export const holdNotification = (notification: DtoIncomingNotification) => {
    const key = callKey(notification.call_sid);
    if (!key) return;
    const expiresAt = pendingPrepares.get(key) || (Date.now() + DEFAULT_HOLD_MS);
    heldNotifications.set(key, { notification, expiresAt });
    log("notification_held", { call_sid: key });
};

export const takeHeldNotification = (callSid: string): DtoIncomingNotification | undefined => {
    const key = callKey(callSid);
    const held = heldNotifications.get(key);
    if (!held) return undefined;
    heldNotifications.delete(key);
    return held.expiresAt >= Date.now() ? held.notification : undefined;
};

export const dropCall = (callSid: string) => {
    const key = callKey(callSid);
    heldNotifications.delete(key);
    pendingPrepares.delete(key);
};

export const logPopupVisible = (notification: DtoIncomingNotification) => {
    log("popup_visible", { call_sid: callKey(notification.call_sid) });
};

const waitForRegistered = (device: any): Promise<void> => new Promise((resolve) => {
    if (device.state === "registered") return resolve();
    device.once("registered", () => resolve());
});

const timeout = (ms: number) => new Promise<"timeout">((resolve) => setTimeout(() => resolve("timeout"), ms));

// A call (incoming, active, held, outbound) currently lives on the Device.
const hasCallOnDevice = (): boolean => {
    const state = store.getState().agentNotification;
    if (state.audioNotificationData || state.holdCallDetails || state.callDetails || state.outBoundCallConnection) return true;
    return (state.voiceCallInstance || []).some((call: any) => {
        try {
            return ACTIVE_CALL_STATUSES.includes(call.status());
        } catch (e) {
            return false;
        }
    });
};

const recordLifecycleFailure = (event: string, details: any) => {
    consecutiveFailures += 1;
    log(event, { ...details, consecutive_failures: consecutiveFailures });
};

// Replaces a Device that keeps failing to (un)register. Never while a call is on it: the existing
// App effect destroys the previous Device once the store holds the new one.
const recreateDeviceIfStuck = (token: string | undefined) => {
    if ((consecutiveFailures < MAX_LIFECYCLE_FAILURES && !deviceSuspect) || !deviceRecreator || !token) return;
    if (hasCallOnDevice()) {
        log("device_recreate_deferred", { reason: "call_on_device", consecutive_failures: consecutiveFailures });
        return;
    }
    log("device_recreate", { consecutive_failures: consecutiveFailures, previous_state: getDevice()?.state });
    try {
        deviceRecreator(token);
        registerInFlight = null;
        unregisterInFlight = null;
        consecutiveFailures = 0;
        deviceSuspect = false;
    } catch (e) {
        log("device_recreate_failed", { message: (e as any)?.message });
    }
};

// Registers the Device (single flight, bounded) and resolves ok only when it is registered in time.
// A timeout clears the in-flight state so a later prepare can try again or recreate the Device.
const ensureRegistered = async (token: string | undefined, timeoutMs: number): Promise<{ ok: boolean; reason?: string }> => {
    recreateDeviceIfStuck(token);
    const device = getDevice();
    if (!device) return { ok: false, reason: "no_device" };
    if (device.state === "destroyed") return { ok: false, reason: "device_destroyed" };
    try {
        if (token) device.updateToken(token);
    } catch (e) {
        return { ok: false, reason: "update_token_failed" };
    }
    if (unregisterInFlight) {
        await Promise.race([unregisterInFlight.catch(() => undefined), timeout(timeoutMs)]);
    }
    if (device.state === "registered") {
        consecutiveFailures = 0;
        return { ok: true };
    }
    if (!registerInFlight) {
        const current: Promise<void> = device.state === "unregistered" ? device.register() : waitForRegistered(device);
        registerInFlight = current;
        current.catch(() => undefined).finally(() => {
            if (registerInFlight === current) registerInFlight = null;
        });
    }
    const pending = registerInFlight!;
    const result = await Promise.race([
        pending.then(() => "ok" as const, (e: any) => `error:${e?.message || "register_failed"}`),
        timeout(timeoutMs),
    ]);
    if (result === "ok" && getDevice()?.state === "registered") {
        consecutiveFailures = 0;
        return { ok: true };
    }
    // Never leave a hung register() blocking every future prepare.
    if (registerInFlight === pending) registerInFlight = null;
    recordLifecycleFailure("register_failed", { reason: String(result), state: getDevice()?.state });
    return { ok: false, reason: String(result) };
};

// prepare_call from the backend: register and ack { ready }. The ack belongs to this prepare.
export const handlePrepareCall = async (data: any, ack: Function) => {
    const key = callKey(data?.call_sid);
    const reply = (payload: any) => {
        try {
            if (typeof ack === "function") ack(payload);
        } catch (e) { /* socket gone */ }
    };
    if (!key) return reply({ ready: false, reason: "missing_call_sid" });
    const backendTimeout = Number(data?.registration_timeout_ms) > 0 ? Number(data.registration_timeout_ms) : DEFAULT_LIFECYCLE_TIMEOUT_MS;
    lifecycleTimeoutMs = backendTimeout;
    // Answer slightly inside the backend's ack timeout.
    const localTimeout = Math.max(1000, backendTimeout - 500);
    const holdMs = Number(data?.hold_registration_ms) > 0 ? Number(data.hold_registration_ms) : DEFAULT_HOLD_MS;
    pendingPrepares.set(key, Date.now() + holdMs);
    log("register_started", { call_sid: key, state: getDevice()?.state });
    const result = await ensureRegistered(data?.token, localTimeout);
    if (result.ok) {
        log("registered", { call_sid: key });
    } else {
        pendingPrepares.delete(key);
        log("registration_failed", { call_sid: key, reason: result.reason });
    }
    reply({ ready: result.ok, reason: result.reason });
};

const purgeExpired = () => {
    const now = Date.now();
    pendingPrepares.forEach((expiresAt, key) => {
        if (expiresAt < now) pendingPrepares.delete(key);
    });
    heldNotifications.forEach((held, key) => {
        if (held.expiresAt < now) heldNotifications.delete(key);
    });
};

// Anything that still needs incoming calls on this Device: a pending prepare, a held or visible
// voice notification, an active or held call (resume re-dials this identity), a third-agent leg.
const hasActiveUsage = (): boolean => {
    const state = store.getState().agentNotification;
    if (pendingPrepares.size > 0 || heldNotifications.size > 0) return true;
    if ((state.notificationData || []).some((n: DtoIncomingNotification) => n.call_type === "voice")) return true;
    return hasCallOnDevice();
};

// Bounded unregister: a hung unregister() is released after the lifecycle timeout.
const unregisterDevice = (device: any, reason: string) => {
    log("unregister", { reason });
    const current: Promise<void> = device.unregister();
    unregisterInFlight = current;
    current.catch((e: any) => log("unregister_failed", { message: e?.message }))
        .finally(() => {
            if (unregisterInFlight === current) unregisterInFlight = null;
        });
    timeout(lifecycleTimeoutMs).then(() => {
        if (unregisterInFlight === current) {
            unregisterInFlight = null;
            deviceSuspect = true;
            recordLifecycleFailure("unregister_timeout", { reason, state: getDevice()?.state });
        }
    });
};

// Unregisters an idle Device in on-demand mode. Never destroys it, never runs during a call.
export const maybeUnregister = (reason: string = "idle") => {
    if (!onDemandMode) return;
    purgeExpired();
    const device = getDevice();
    if (!device || device.state !== "registered" || registerInFlight || unregisterInFlight) {
        idleSince = null;
        return;
    }
    if (hasActiveUsage() || store.getState().agentNotification.appLoader) {
        idleSince = null;
        return;
    }
    const now = Date.now();
    if (idleSince === null) {
        idleSince = now;
        return;
    }
    if (now - idleSince < IDLE_GRACE_MS) return;
    idleSince = null;
    unregisterDevice(device, reason);
};

// Mode may change after a reconnect (backend deploy). Restores legacy registration when the
// flag is turned off; idle-unregisters when it is turned on.
export const applyRegistrationMode = (value: boolean) => {
    const previous = onDemandMode;
    setOnDemandMode(value);
    if (previous === onDemandMode) return;
    log("mode_changed", { on_demand: onDemandMode });
    const device = getDevice();
    if (!onDemandMode && device && device.state === "unregistered" && !registerInFlight) {
        const current: Promise<void> = device.register();
        registerInFlight = current;
        current.catch((e: any) => log("legacy_register_failed", { message: e?.message }))
            .finally(() => {
                if (registerInFlight === current) registerInFlight = null;
            });
    } else if (onDemandMode) {
        maybeUnregister("mode_changed");
    }
};

// Logout: release the registration slot unless a call is still using the Device.
export const releaseOnLogout = () => {
    pendingPrepares.clear();
    heldNotifications.clear();
    if (!onDemandMode) return;
    const device = getDevice();
    if (!device || device.state !== "registered" || registerInFlight || unregisterInFlight || hasActiveUsage()) return;
    unregisterDevice(device, "logout");
};
